import type { ComponentProps, ReactNode } from "react";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "src/components/ui/alert-dialog";

interface ConfirmDialogBaseProps {
	/** Runs when the user confirms. */
	mutateFn: () => void;
	header?: ReactNode;
	subHeader?: ReactNode;
	confirmButtonText?: ReactNode;
	confirmButtonProps?: Partial<ComponentProps<typeof AlertDialogAction>>;
}

const defaults = {
	header: "Are you sure you want to perform this action?",
	subHeader: "Are you sure? This process cannot be undone",
	confirmButtonText: "Yes, Continue",
} as const;

interface CustomConfirmDialogProps extends ConfirmDialogBaseProps {
	/** Element that opens the dialog. */
	trigger: ReactNode;
}

/** Confirmation prompt that opens from its own trigger. */
const CustomConfirmDialog = ({
	mutateFn,
	trigger,
	header = defaults.header,
	subHeader = defaults.subHeader,
	confirmButtonText = defaults.confirmButtonText,
	confirmButtonProps,
}: CustomConfirmDialogProps) => (
	<AlertDialog>
		<AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
		<AlertDialogContent>
			<AlertDialogHeader>
				<AlertDialogTitle>{header}</AlertDialogTitle>
				<AlertDialogDescription>{subHeader}</AlertDialogDescription>
			</AlertDialogHeader>
			<AlertDialogFooter>
				<AlertDialogCancel>Cancel</AlertDialogCancel>
				<AlertDialogAction {...confirmButtonProps} onClick={mutateFn}>
					{confirmButtonText}
				</AlertDialogAction>
			</AlertDialogFooter>
		</AlertDialogContent>
	</AlertDialog>
);

interface ControlledConfirmDialogProps extends ConfirmDialogBaseProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/** Same prompt, opened by the caller rather than by a trigger element. */
export const CustomConfirmDialogWithoutTrigger = ({
	mutateFn,
	open,
	onOpenChange,
	header = defaults.header,
	subHeader = defaults.subHeader,
	confirmButtonText = defaults.confirmButtonText,
	confirmButtonProps,
}: ControlledConfirmDialogProps) => (
	<AlertDialog open={open} onOpenChange={onOpenChange}>
		<AlertDialogContent>
			<AlertDialogHeader>
				<AlertDialogTitle>{header}</AlertDialogTitle>
				<AlertDialogDescription>{subHeader}</AlertDialogDescription>
			</AlertDialogHeader>
			<AlertDialogFooter>
				<AlertDialogCancel>Cancel</AlertDialogCancel>
				<AlertDialogAction {...confirmButtonProps} onClick={mutateFn}>
					{confirmButtonText}
				</AlertDialogAction>
			</AlertDialogFooter>
		</AlertDialogContent>
	</AlertDialog>
);

export default CustomConfirmDialog;
